// Where the session token lives in a browser, and the argument for it.
//
// This is the most consequential decision in the package, so it is written out
// in full rather than summarised. What is being stored is a credential that, for
// a Nova admin who has proved a second factor, reads every client's attendee
// list across every activation, unscoped. Nothing else in XP1 has that reach.
//
// ==================================================================== the choice
//
// sessionStorage, one key, per tab, and nowhere else. Not localStorage, not a
// cookie, and not memory alone. Each of the three rejections is a different
// argument and none of them is "it was easier".
//
// -------------------------------------------------------------- not localStorage
//
// localStorage survives the browser being closed and reopened, and it is shared
// by every tab on the origin. Both properties are wrong here in the same
// direction: they make the token's lifetime a function of the storage rather
// than of the person. A client's marketing manager signs in on a Tuesday on a
// laptop that then goes in a bag, and the credential is on that disk until
// auth_session.expires_at passes — the person is gone, the tab is gone, the
// browser has restarted twice, and the token still works. A machine that is
// shared, or borrowed, or stolen with the lid closed, hands the next person a
// live session with no login.
//
// It is also the shape that makes a script injection maximally profitable. An
// injected script can read localStorage for an origin it is running on
// regardless of which tab wrote it and regardless of whether anybody is signed
// in at that moment, so the window for a stored-XSS payload to harvest a token
// is every visit rather than the visit it was injected into.
//
// ------------------------------------------------------------------- not a cookie
//
// An HttpOnly cookie is the genuinely stronger answer and it is not available,
// for a reason worth recording because it looks like an oversight.
//
// The API is on a different site from the console. The console is to be
// xp.novaexhibits.com and the service is a Cloud Run host, so a cookie the
// browser would send to the API is a third-party cookie: it needs SameSite=None,
// and Safari blocks it outright while Chrome's own policy on it keeps moving. A
// credential that stops working when a browser ships a privacy change is not a
// credential.
//
// The obvious repair is to put both on novaexhibits.com and scope the cookie to
// the parent domain — and that repair is FORBIDDEN by a ruling already made. The
// console is on its own host precisely so that a script injected into
// admin.novaexhibits.com, which is Nova's orders, revenue and Stripe admin,
// cannot reach client attendee data. A `.novaexhibits.com` cookie is readable by
// every host under it, which hands the session straight back to the surface the
// separate host exists to keep away from it. So the parent-domain cookie is not
// a shortcut we declined; it is the specific thing the host separation was for.
//
// The repair that does work is same-origin: the API reachable at a path under
// xp.novaexhibits.com, through a reverse proxy, so a Lax cookie scoped to that
// exact host is first-party. That is a deployment change and a server change —
// the login route returns the token in a response body today and sets no cookie
// — and it brings CSRF protection with it, because a cookie the browser attaches
// automatically is a cookie an attacker's page can cause to be attached. It is
// the right next step and it is a larger job than this one. It is written up in
// xp/CONSOLE-REPORT.md rather than half-built here.
//
// ------------------------------------------------------------ not memory alone
//
// A token in a JavaScript variable and nowhere else is the answer that sounds
// strongest, and most of the strength is an illusion. The threat it is imagined
// to stop is a script reading the token, and a script that is running in this
// page can read a variable in this page just as easily — or skip the token
// entirely and make the requests itself from inside the session. What memory
// genuinely buys over sessionStorage is narrow: the token is not present during
// a session restore, which is the one real difference and is named below.
//
// What it costs is not narrow. Every reload, every "open in new tab", every
// crash recovery means a new login, and a login here is an email round trip: a
// six-digit code, a ten-minute window, and a wait on Mailgun. Ten of those a day
// is a person who keeps one tab open for a fortnight to avoid it, which converts
// the property into a longer-lived session rather than a shorter one. It also
// multiplies the number of live login codes in flight, on a path whose whole
// design is that a code is short-lived and single-use.
//
// ============================================ what sessionStorage actually costs
//
// Stated rather than glossed, because a decision defended without its cost is a
// decision nobody can revisit.
//
//   A SESSION RESTORE WRITES IT TO DISK. Chrome and Firefox both restore
//   sessionStorage when a window is reopened after a crash or through "restore
//   previous session", which means the value is persisted somewhere on disk
//   between those two moments. It is a narrower window than localStorage's — it
//   requires the browser to have been closed unexpectedly or restored
//   deliberately, rather than merely closed — but it is not zero, and anybody
//   who reads this file should know it is not zero.
//
//   AN INJECTED SCRIPT IN THIS TAB CAN STILL READ IT. Nothing storage-side fixes
//   that; index.html's content security policy and the absence of any
//   `dangerouslySetInnerHTML` in this package are what stand between us and it.
//
//   IT IS NOT SHARED BETWEEN TABS, WHICH IS A COST AND A FEATURE. Opening a
//   second tab means signing in again. That is deliberate: a token that follows
//   a person into every tab they open is a token whose blast radius is their
//   whole browsing session. It will annoy somebody, and the answer to that
//   complaint is not localStorage.
//
// ================================================== the rules around the storage
//
// The storage choice is the smaller half. These are the rest, and each one is
// asserted in check.mjs by looking at the world rather than by counting calls:
//
//   THE TOKEN GOES NOWHERE ELSE. Not in a URL, not in a query string, not in a
//   log line, not in a React prop that ends up serialised. It is read out of
//   here immediately before a request and not held anywhere in between.
//
//   A 401 CLEARS IT, ALWAYS. An expired or revoked session is indistinguishable
//   from a wrong token by design — reports/auth.ts answers one 401 for four
//   causes — so the console must not try to tell them apart and must not retry.
//   It forgets the token and says so.
//
//   A 403 DOES NOT CLEAR IT. This is the one that would be a bug if it were
//   symmetrical. A nova_admin who signed in without a second factor holds a
//   perfectly live session and is refused the reports; clearing it would sign
//   them out, send them back to a login form, and produce exactly the same 403
//   on the next attempt. An infinite loop through an email inbox.
//
//   EXPIRY IS CHECKED ON READ, AND IS NOT AUTHORITY. A stored session past its
//   own expires_at is dropped here rather than sent, which saves a pointless
//   round trip and a confusing sign-out. The server remains the authority: it
//   knows about revocation, which this cannot see, and about a disabled
//   identity, which it also cannot.
//
//   ANYTHING THAT DOES NOT PARSE IS CLEARED RATHER THAN TRUSTED. A half-written
//   value is either a browser that failed mid-write or somebody editing storage
//   by hand, and the safe reading of both is "no session".
//
// ============================================ the token now changes underneath
//
// From 2026-08-29 the service rotates the session token on use, and every
// paragraph above is unchanged by it. The value in this one key is replaced; the
// key, the storage, and every argument for the storage are the same. Nothing
// here moved to a cookie, to localStorage, or to memory — that question is open
// and is written up in xp/CONSOLE-REPORT.md, and rotation is deliberately not an
// answer to it. What rotation does is shorten how long a token that leaks out of
// here is worth having, which is a different axis from where it is kept and
// makes the storage question no less worth settling.
//
// WHY THE ADOPTION IS A HANDLE (`SessionToken`) AND NOT SIMPLY A NEW `Session`
// IN REACT STATE. This is the single most consequential shape in the console
// half of the change, so it is argued here rather than at the call site.
//
// The naive version puts the replacement in state: `setSession({...s, token})`.
// Every surface takes `token` as a prop, and every surface has a
// `useEffect(..., [api, token, activationId, onSessionEnded])` that fetches. A
// new token is a new prop, a new prop re-runs the effect, the fetch rotates
// again, and the console fetches the same report forever — a loop that is
// invisible in development because it looks like a page that loaded, and shows
// up as a client's report_access filling with hundreds of rows for one read.
//
// So the token is a value behind a stable reference. The handle's identity never
// changes for the life of a sign-in, `current()` answers with whatever the
// latest token is, and `adopt` writes the replacement here and into storage.
// React sees nothing change, because from React's point of view nothing did:
// which report is on screen did not change, and that is what the effects are
// about.
//
// THE COST, WHICH IS REAL. The token is now mutable state outside React, which
// is the thing React exists to discourage. What makes it acceptable is that
// nothing renders it and nothing branches on it — it is written in exactly one
// place, read in exactly one place, and both are in api/client.ts. The moment
// something wants to *display* the token, this shape is wrong and the argument
// has to be reopened.

/** The one key. Namespaced, because an origin may one day hold more than this. */
export const SESSION_KEY = "nova.xp.console.session";

/**
 * The last hall-screen URL this sign-in minted or pasted, per activation.
 *
 * Not the session token. A view URL is a rotation credential, weaker than the
 * admin session already in this store, and it is forgotten with that session.
 * session/kioskView.ts is the only writer of the value.
 */
export const KIOSK_VIEW_KEY = "nova.xp.console.kioskView";

/**
 * What the login surface hands back, kept exactly as far as it is useful.
 *
 * `role` and `clientId` are stored, and it is worth being explicit that they are
 * NOT authority. They decide what is drawn — an admin sees a different landing
 * than a client viewer — and every request is still answered by a server that
 * re-derives both from the session on its own side. api/src/auth/routes.ts says
 * why it sends them and why it deliberately withholds the identity id: that is
 * the value 004's policies compare against, and a client holding a copy is a
 * client that could send a different one. Nothing here re-adds it.
 */
export type Session = {
  readonly token: string;
  readonly sessionId: string;
  /** ISO 8601, from the server's clock. Compared against this browser's, which is the flaw named in `live`. */
  readonly expiresAt: string;
  readonly role: string;
  readonly clientId: string | null;
  /**
   * Whether the service said this session's token rotates.
   *
   * The server's answer, stored, rather than what this console asked for.
   * api/src/auth/routes.ts argues why it echoes the decision back instead of
   * letting a client infer it from its own request; storing that echo is the
   * other half. A console that assumed rotation because it had asked for it
   * would watch for a header that never comes, and one that assumed no rotation
   * would ignore a replacement it was handed.
   *
   * It changes nothing about what is sent — the token goes in the same header
   * either way — so nothing branches on it in the request path. It exists so a
   * reload can tell whether a stored session is one of the rotating ones without
   * asking, and so check.mjs can assert the two halves agree.
   */
  readonly rotatesOnUse: boolean;
  /**
   * Whether this session proved a second factor.
   *
   * The server's `second_factor_proved`, stored, which is
   * `auth_session.second_factor_id` as a boolean — what THIS SESSION proved, not
   * whether the account is enrolled. api/src/auth/routes.ts argues the difference
   * at the send: false means this session proved no factor, and must never be
   * read as "the account has none". There is no GET that reads it back,
   * deliberately; a reload keeps the value the way it already keeps `role`.
   *
   * `null` is an older stored session that predates the field. It is not false.
   * parseSession is the one place that distinction is made: a 200 without the
   * field is not a session (`asSession` refuses it), and a stored value without
   * the field is a session whose answer is unknown. Defaulting the latter to
   * false would tell somebody who used an authenticator this morning that they
   * have none.
   */
  readonly secondFactorProved: boolean | null;
  /**
   * Whether this person's export switch was on when the session opened.
   *
   * The service's `can_export` (149), stored the way `role` is: what was true
   * at sign-in, deciding what is drawn and nothing about access. The export
   * route reads the switch again on every request, so an Admin turning it off
   * on Team takes effect there at once and here at the next sign-in. True for
   * every Admin, by ruling.
   *
   * `null` is a session stored before the console kept the field, or a body
   * that did not carry it. Unknown, not off: surfaces/roleAccess.ts decides
   * what an unknown switch shows.
   */
  readonly canExport: boolean | null;
  /**
   * The address this sign-in was made with, as it was typed into the form.
   *
   * NOT FROM THE SERVER, AND THAT IS THE WHOLE OF WHY IT IS ALLOWED TO BE HERE.
   * api/src/auth/routes.ts sends `role`, `client_id`, `session_id`,
   * `expires_at` and two booleans, and no address; it is explicit that there is
   * no route which reads a session back, because a GET that answered for a
   * bearer token would answer somebody who found one. So this cannot be
   * fetched, and nothing here fetches it. It is the string the person put in
   * the first field of the login form, kept, and showing it to them is showing
   * them their own typing.
   *
   * WHICH MEANS IT IS THE ADDRESS THEY USED AND NOT THE ADDRESS ON THE RECORD.
   * The two are the same account and can differ character for character:
   * signIn.ts sends the address exactly as typed because normalisation belongs
   * to the server, so somebody who typed a capital letter signed in fine and
   * has a capital letter here where the row in `identity` has none. Account.tsx
   * therefore labels it as the address this sign-in used rather than as the
   * account's email address, and that wording is load-bearing rather than
   * cautious.
   *
   * `null` is a session stored before this build, on exactly the reasoning
   * `secondFactorProved` uses for the same case: absent is unknown. The screens
   * that draw it leave the line out rather than guessing.
   */
  readonly email: string | null;
};

/**
 * The token, behind a reference that does not change when the token does.
 *
 * See the header for the loop this shape exists to prevent. The two methods are
 * the whole surface, and they are deliberately not a getter and a setter on an
 * object somebody could spread: `{...token}` would produce a snapshot that looks
 * like a token and stops updating, which is the failure this is preventing,
 * wearing the shape of the fix.
 */
export type SessionToken = {
  /** The token to put in the next Authorization header. */
  current(): string;
  /**
   * Take a replacement the service handed back.
   *
   * Returns nothing, and cannot fail in a way a caller could act on. A browser
   * that refuses to store is already handled — `write` returns false and App.tsx
   * has already told the person this sign-in lasts as long as the tab — and the
   * in-memory value is updated regardless, so the console keeps working for the
   * life of the page either way. Throwing here would turn a storage quirk into a
   * failed report.
   */
  adopt(replacement: string): void;
};

/**
 * A handle over one session, writing replacements back to the store.
 *
 * Takes the session by value and the store separately, rather than reading the
 * session out of the store on every `current()`. A read per request would be a
 * `JSON.parse` on the hot path and, worse, would let a session cleared in
 * another frame come back as a null nobody expected. The handle is created when
 * a session begins and discarded when it ends.
 */
export function sessionToken(store: SessionStore, session: Session): SessionToken {
  let held = session;

  return {
    current: () => held.token,
    adopt(replacement: string): void {
      // Memory first, storage second, and the order is the one that matters
      // under a browser that refuses to store: the next request must carry the
      // new token whether or not the disk took it. The reverse order would leave
      // a refused write followed by a request carrying a token the service has
      // already filed as spent, which one grace window later is a revocation.
      held = { ...held, token: replacement };
      store.write(held);
    },
  };
}

/**
 * The two lines of the browser API this package touches.
 *
 * An interface rather than direct calls to `window.sessionStorage`, for one
 * reason that is about the checks and one that is about Safari. The checks drive
 * a real store and a hostile one — a store that throws on write is what private
 * browsing used to be, and a console that crashed on it would be a console
 * nobody could sign into on a locked-down machine. The narrow shape is also what
 * lets check.mjs hand in a store that records every write and then assert what
 * was written, which is a different observation from asserting what was called.
 */
export interface KeyedStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type SessionStore = {
  /** The live session, or null. Never a session past its own expiry. */
  read(): Session | null;
  /** Returns false where the browser refused to store it, which is not an error. */
  write(session: Session): boolean;
  clear(): void;
};

/**
 * Whether a stored session is still worth sending, by this browser's clock.
 *
 * A minute of slack in the wrong direction on purpose. A browser clock that is
 * a few seconds ahead of the server's would otherwise discard a session the
 * server would have honoured, and the visible result is a person being signed
 * out mid-task for no reason they can see. Being a minute too generous costs one
 * request that comes back 401 and is handled.
 */
const CLOCK_SLACK_MS = 60_000;

export function sessionOnStore(store: KeyedStore, now: () => number = Date.now): SessionStore {
  const clear = (): void => {
    try {
      store.removeItem(SESSION_KEY);
      store.removeItem(KIOSK_VIEW_KEY);
    } catch {
      // A store that refuses to delete is a store we cannot clean, and throwing
      // here would turn signing out into an error message. The token is already
      // forgotten in memory and the server-side revoke has already been asked
      // for by whoever called this, which is the half that actually matters.
    }
  };

  return {
    read(): Session | null {
      let raw: string | null;
      try {
        raw = store.getItem(SESSION_KEY);
      } catch {
        return null;
      }
      if (raw === null) return null;

      const session = parseSession(raw);
      if (session === null) {
        // Cleared rather than left. A value that does not parse will not parse
        // on the next read either, and leaving it means every page load does
        // this work and reports no session for a reason nobody can see.
        clear();
        return null;
      }

      if (Date.parse(session.expiresAt) + CLOCK_SLACK_MS <= now()) {
        clear();
        return null;
      }

      return session;
    },

    write(session: Session): boolean {
      try {
        store.setItem(SESSION_KEY, JSON.stringify(session));
        return true;
      } catch {
        // Safari's private browsing used to throw here, and a locked-down
        // corporate profile still can. False rather than a throw, because the
        // console can run perfectly well for the life of one page with the
        // session in memory — what it cannot do is survive a reload, and the
        // caller is the one that knows whether to say so.
        return false;
      }
    },

    clear,
  };
}

/**
 * The browser's own store, or a store that holds nothing.
 *
 * Reaching for `window.sessionStorage` can itself throw in a browser configured
 * to block storage, which is why this is a function with a try in it rather than
 * a constant. The fallback is deliberately not an in-memory shim pretending to
 * be storage: a shim would make `write` report success and then lose the value
 * on reload, and the console would tell somebody they were signed in for as long
 * as they did not touch anything.
 */
export function browserSessionStore(): KeyedStore {
  try {
    const storage = window.sessionStorage;
    // Probed rather than assumed present. Some environments expose the object
    // and throw on use, which would otherwise surface as a crash at sign-in
    // rather than as a degraded sign-in.
    const probe = `${SESSION_KEY}.probe`;
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return {
      getItem: () => null,
      setItem: () => {
        throw new Error("this browser is not allowing the console to store a session");
      },
      removeItem: () => {},
    };
  }
}

/**
 * A stored value back into a session, or null.
 *
 * Every field is checked rather than cast. The value being parsed came out of a
 * store a person can edit in a developer console, so `JSON.parse` followed by
 * `as Session` would be a type assertion standing in for a validation — and the
 * field it would wave through is `token`, which is then put in an Authorization
 * header.
 */
function parseSession(raw: string): Session | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;

  const token = row["token"];
  const sessionId = row["sessionId"];
  const expiresAt = row["expiresAt"];
  const role = row["role"];
  const clientId = row["clientId"];
  const rotatesOnUse = row["rotatesOnUse"];
  const secondFactorProvedRaw = row["secondFactorProved"];

  if (typeof token !== "string" || token === "") return null;
  if (typeof sessionId !== "string" || sessionId === "") return null;
  if (typeof expiresAt !== "string" || Number.isNaN(Date.parse(expiresAt))) return null;
  if (typeof role !== "string" || role === "") return null;
  if (clientId !== null && typeof clientId !== "string") return null;
  // Refused rather than defaulted, on the same reasoning as every field above:
  // this value came out of a store a person can edit. Defaulting a missing one
  // to false would also silently downgrade a session written by an older build,
  // and a session that has stopped watching for replacements is a session that
  // gets revoked for reuse the moment the service rotates it.
  if (typeof rotatesOnUse !== "boolean") return null;
  // Absent is unknown, not false, and not a reason to drop the session. False
  // means this session proved no factor — routes.ts is explicit that it must
  // never be read as "the account has none" — and an older tab never recorded
  // the answer. Defaulting that to false would show "you have no authenticator"
  // to somebody who used one this morning. Kicking them out would be the other
  // wrong reading of the same absence.
  const secondFactorProved =
    typeof secondFactorProvedRaw === "boolean" ? secondFactorProvedRaw : null;

  // Absent is unknown here too, for the reason on the field: a tab that signed
  // in before the console kept the address is a tab whose session is otherwise
  // perfectly good, and dropping it would sign somebody out to gain a line of
  // text. An empty string is treated as absent rather than shown, because a
  // blank where an address should be reads as a fault in the account.
  const emailRaw = row["email"];
  const email = typeof emailRaw === "string" && emailRaw.trim() !== "" ? emailRaw : null;

  // Absent is unknown, on secondFactorProved's reasoning: a tab signed in
  // before the console kept the switch is otherwise a perfectly good session.
  const canExportRaw = row["canExport"];
  const canExport = typeof canExportRaw === "boolean" ? canExportRaw : null;

  return { token, sessionId, expiresAt, role, clientId, rotatesOnUse, secondFactorProved, canExport, email };
}
